import React, { useState } from 'react';
import { 
  Modal, 
  Card, 
  Descriptions, 
  Tag, 
  Button, 
  Typography, 
  Space, 
  Row, 
  Col, 
  Progress, 
  Badge, 
  Avatar, 
  Form,
  Input,
  Checkbox,
  DatePicker
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  TeamOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { COLORS } from '../../constants/colors';
import EventService from '../../services/EventService';

const { Title, Text, Paragraph } = Typography;

const EventDetailsModal = ({ 
  event, 
  isOpen, 
  onClose, 
  onRegister,
  userRegistered = false,
  showRegistration = true 
}) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [form] = Form.useForm();

  if (!isOpen || !event) return null;

  const handleRegister = async (values) => {
    if (!values.consentGiven) {
      alert('Please provide consent to proceed with registration.');
      return;
    }

    setLoading(true);
    try {
      await EventService.registerForEvent(event._id, values);
      if (onRegister) {
        onRegister(event._id, values);
      }
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error registering for event:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: COLORS.textMuted,
      published: COLORS.info,
      registration_open: COLORS.healthyGreen,
      registration_closed: COLORS.warningAmber,
      in_progress: COLORS.$mainRed,
      completed: COLORS.success,
      cancelled: COLORS.error,
      postponed: COLORS.warningAmber
    };
    return colors[status] || COLORS.textMuted;
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: <ExclamationCircleOutlined />,
      published: <CheckCircleOutlined />,
      registration_open: <CheckCircleOutlined />,
      registration_closed: <ExclamationCircleOutlined />,
      in_progress: <ClockCircleOutlined />,
      completed: <CheckCircleOutlined />,
      cancelled: <ExclamationCircleOutlined />,
      postponed: <ExclamationCircleOutlined />
    };
    return icons[status] || <ExclamationCircleOutlined />;
  };

  const getTypeIcon = (type) => {
    const icons = {
      health_screening: <HeartOutlined />,
      blood_drive: <HeartOutlined />,
      community_outreach: <TeamOutlined />,
      educational_workshop: <CalendarOutlined />,
      fundraiser: <CalendarOutlined />,
      volunteer_training: <TeamOutlined />,
      awareness_campaign: <CalendarOutlined />,
      other: <CalendarOutlined />
    };
    return icons[type] || <CalendarOutlined />;
  };

  const capacityPercentage = event.capacity ? (event.capacity.registered / event.capacity.total) * 100 : 0;
  const isNearCapacity = capacityPercentage > 80;
  const isFull = capacityPercentage >= 100;
  const healthScreeningOptions = event.healthScreeningOptions || {};
  const availableScreenings = Object.entries(healthScreeningOptions)
    .filter(([, value]) => value)
    .map(([key]) => key);

  const renderDetailsTab = () => (
    <div className="event-details-content">
      <div className="status-indicator" data-status={event.status}>
        {getStatusIcon(event.status)}
        <Text strong style={{ color: getStatusColor(event.status) }}>
          {event.status?.replace('_', ' ').toUpperCase()}
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          {/* Basic Information */}
          <Card
            title={
              <Space>
                <CalendarOutlined style={{ color: COLORS.$mainRed }} />
                Event Information
              </Space>
            }
            className="detail-card"
          >
            <Title level={4} style={{ margin: '0 0 16px 0', color: COLORS.textDark }}>
              {event.title}
            </Title>
            
            <Paragraph style={{ color: COLORS.textMedium, marginBottom: 16 }}>
              {event.description}
            </Paragraph>

            <Descriptions column={1} size="small">
              <Descriptions.Item label="Event Type">
                <Tag color={COLORS.$mainRed} style={{ borderRadius: 4 }}>
                  {getTypeIcon(event.type)} {event.type?.replace('_', ' ').toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date & Time">
                <Space direction="vertical" size={0}>
                  <Text>
                    <CalendarOutlined style={{ marginRight: 8, color: COLORS.$mainRed }} />
                    {dayjs(event.startDate).format('MMMM DD, YYYY')}
                  </Text>
                  <Text type="secondary">
                    <ClockCircleOutlined style={{ marginRight: 8 }} />
                    {dayjs(event.startDate).format('h:mm A')} - {dayjs(event.endDate).format('h:mm A')}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Duration">
                {dayjs(event.endDate).diff(dayjs(event.startDate), 'hour')} hours
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Location Details */}
          <Card
            title={
              <Space>
                <EnvironmentOutlined style={{ color: COLORS.$mainRed }} />
                Location Details
              </Space>
            }
            className="detail-card"
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Venue">
                <Text strong>{event.location?.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                <Space direction="vertical" size={0}>
                  <Text>{event.location?.address?.street}</Text>
                  <Text>
                    {event.location?.address?.city}, {event.location?.address?.state} {event.location?.address?.zipCode}
                  </Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Health Screening Options */}
          {event.type === 'health_screening' && availableScreenings.length > 0 && (
            <Card
              title={
                <Space>
                  <HeartOutlined style={{ color: COLORS.$mainRed }} />
                  Available Health Screenings
                </Space>
              }
              className="detail-card"
            >
              <Row gutter={[8, 8]}>
                {availableScreenings.map(screening => (
                  <Col key={screening}>
                    <Tag 
                      color={COLORS.healthyGreen}
                      style={{ borderRadius: 4, padding: '4px 8px' }}
                    >
                      <CheckCircleOutlined style={{ marginRight: 4 }} />
                      {screening.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          {/* Registration Stats */}
          {event.capacity && (
            <Card
              title={
                <Space>
                  <TeamOutlined style={{ color: COLORS.$mainRed }} />
                  Registration Status
                </Space>
              }
              className="detail-card"
            >
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text type="secondary">Capacity</Text>
                  <Progress
                    percent={Math.round(capacityPercentage)}
                    status={isFull ? 'exception' : isNearCapacity ? 'active' : 'normal'}
                    strokeColor={isFull ? COLORS.error : isNearCapacity ? COLORS.warningAmber : COLORS.healthyGreen}
                    style={{ marginBottom: 8 }}
                  />
                  <Row justify="space-between">
                    <Text strong style={{ color: COLORS.textDark }}>
                      {event.capacity.registered} / {event.capacity.total} registered
                    </Text>
                    <Badge 
                      count={event.capacity.waitlist} 
                      style={{ backgroundColor: COLORS.warningAmber }}
                      title="Waitlisted"
                    />
                  </Row>
                </div>

                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Available Spots">
                    <Text style={{ 
                      color: event.capacity.total - event.capacity.registered > 0 ? COLORS.healthyGreen : COLORS.error,
                      fontWeight: 'bold'
                    }}>
                      {Math.max(0, event.capacity.total - event.capacity.registered)}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Waitlist">
                    <Text style={{ color: COLORS.warningAmber, fontWeight: 'bold' }}>
                      {event.capacity.waitlist}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
          )}

          {/* Quick Stats */}
          <Card
            title={
              <Space>
                <UserOutlined style={{ color: COLORS.$mainRed }} />
                Event Statistics
              </Space>
            }
            className="detail-card"
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Created By">
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  {event.createdBy?.fullName || 'Unknown'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Registration Required">
                <Tag color={event.registrationRequired ? COLORS.healthyGreen : COLORS.textMuted}>
                  {event.registrationRequired ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Waitlist Allowed">
                <Tag color={event.allowWaitlist ? COLORS.healthyGreen : COLORS.textMuted}>
                  {event.allowWaitlist ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderRegistrationTab = () => (
    <div className="registration-content">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleRegister}
        className="registration-form"
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter your first name' }]}
            >
              <Input placeholder="Enter your first name" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter your last name' }]}
            >
              <Input placeholder="Enter your last name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="Enter your email address" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[{ required: true, message: 'Please enter your phone number' }]}
            >
              <Input placeholder="Enter your phone number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Date of Birth"
              name="dateOfBirth"
              rules={[{ required: true, message: 'Please select your date of birth' }]}
            >
              <DatePicker 
                placeholder="Select your date of birth" 
                style={{ width: '100%' }}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Emergency Contact"
              name="emergencyContact"
              rules={[{ required: true, message: 'Please enter emergency contact name' }]}
            >
              <Input placeholder="Emergency contact name" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Emergency Contact Phone"
          name="emergencyPhone"
          rules={[{ required: true, message: 'Please enter emergency contact phone' }]}
        >
          <Input placeholder="Emergency contact phone number" />
        </Form.Item>

        <Form.Item
          label="Medical Conditions"
          name="medicalConditions"
        >
          <Input.TextArea 
            placeholder="Please list any medical conditions or write 'None'" 
            rows={3}
          />
        </Form.Item>

        <Form.Item
          label="Current Medications"
          name="medications"
        >
          <Input.TextArea 
            placeholder="Please list current medications or write 'None'" 
            rows={3}
          />
        </Form.Item>

        <Form.Item
          label="Allergies"
          name="allergies"
        >
          <Input.TextArea 
            placeholder="Please list any allergies or write 'None'" 
            rows={3}
          />
        </Form.Item>

        <div className="consent-section">
          <Form.Item
            name="consentGiven"
            valuePropName="checked"
            rules={[{ required: true, message: 'You must provide consent to register' }]}
          >
            <Checkbox>
              <Text strong>
                I consent to participate in this health screening event and understand that results 
                will be kept confidential in accordance with HIPAA regulations.
              </Text>
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="marketingConsent"
            valuePropName="checked"
          >
            <Checkbox>
              I would like to receive updates about future Black Heart Association events and health resources.
            </Checkbox>
          </Form.Item>
        </div>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            style={{ 
              background: COLORS.$mainRed, 
              borderColor: COLORS.$mainRed,
              width: '100%',
              height: '40px'
            }}
          >
            Register for Event
          </Button>
        </Form.Item>
      </Form>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          {getTypeIcon(event.type)}
          Event Details
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1000}
      className="event-details-modal"
    >
      <div className="modal-tabs">
        <div className="tab-buttons">
          <Button
            type={activeTab === 'details' ? 'primary' : 'default'}
            onClick={() => setActiveTab('details')}
            style={{
              background: activeTab === 'details' ? COLORS.$mainRed : 'transparent',
              borderColor: COLORS.$mainRed,
              color: activeTab === 'details' ? 'white' : COLORS.$mainRed
            }}
          >
            Event Details
          </Button>
          {showRegistration && !userRegistered && event.status === 'registration_open' && (
            <Button
              type={activeTab === 'registration' ? 'primary' : 'default'}
              onClick={() => setActiveTab('registration')}
              style={{
                background: activeTab === 'registration' ? COLORS.$mainRed : 'transparent',
                borderColor: COLORS.$mainRed,
                color: activeTab === 'registration' ? 'white' : COLORS.$mainRed,
                marginLeft: 8
              }}
            >
              Register
            </Button>
          )}
        </div>
        
        <div className="tab-content">
          {activeTab === 'details' && renderDetailsTab()}
          {activeTab === 'registration' && renderRegistrationTab()}
        </div>
      </div>
    </Modal>
  );
};

export default EventDetailsModal;
